import {
  PeriodLayout,
  hasLimitedPeriodPreview,
  maximumPreviewPeriods,
} from "./periodLayout";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import LocalisedPlot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { useElementSize } from "hooks/useElementSize";
import { illustratePeriodWeights } from "./periodWeightIllustration";

export const PeriodWeightsPlot = ({
  parameters,
  periodLayout,
}: {
  parameters: unknown;
  periodLayout: PeriodLayout;
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { ref, size } = useElementSize();
  const periods = illustratePeriodWeights({ parameters, periodLayout });
  if (periods.length === 0) return null;
  const deployablePeriods = periods.filter(({ deployable }) => deployable);
  const forecastPeriods = periods.filter(({ deployable }) => !deployable);
  return (
    <Stack gap={1}>
      {periodLayout.forecast.maximum !== 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("autoWeightIllustration", {
            deployable: deployablePeriods.length,
            forecast: forecastPeriods.length,
          })}
        </Typography>
      ) : null}
      {hasLimitedPeriodPreview(periodLayout) ? (
        <Typography variant="body2" color="text.secondary">
          {t("autoLimitedPeriodPreview", { count: maximumPreviewPeriods })}
        </Typography>
      ) : null}
      <Box ref={ref} sx={{ "& .js-plotly-plot": { width: "100%" } }}>
        <LocalisedPlot
          useResizeHandler
          data={[
            {
              type: "bar",
              name: t(
                periodLayout.forecast.maximum === 0
                  ? "autoAllPeriods"
                  : "autoDeployablePeriods"
              ),
              x: deployablePeriods.map(({ period }) => period),
              y: deployablePeriods.map(({ weight }) => weight),
              marker: { color: theme.palette.primary.main },
            },
            {
              type: "bar",
              name: t("autoForecastPeriods"),
              x: forecastPeriods.map(({ period }) => period),
              y: forecastPeriods.map(({ weight }) => weight),
              marker: { color: theme.palette.secondary.main },
            },
          ].filter(({ x }) => x.length > 0)}
          layout={{
            height: 280,
            autosize: true,
            width: size?.width,
            margin: { l: 60, r: 20, t: 10, b: 75 },
            xaxis: { title: { text: t("period") }, dtick: 1, fixedrange: true },
            yaxis: {
              title: { text: t("autoPeriodWeight") },
              range: [0, 1.05],
              fixedrange: true,
            },
            legend: { orientation: "h", y: -0.25 },
            dragmode: false,
          }}
          config={{ displayModeBar: false }}
        />
      </Box>
    </Stack>
  );
};
